import { forwardRef, useEffect, useRef } from 'react'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/lib/utils/cn'

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Tri-state: shows a minus glyph and sets the DOM indeterminate flag. */
  indeterminate?: boolean
  /** Label rendered to the right of the box. */
  label?: React.ReactNode
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { indeterminate = false, label, className, disabled, ...rest },
  forwardedRef,
) {
  const innerRef = useRef<HTMLInputElement>(null)
  const setRef = (node: HTMLInputElement | null) => {
    innerRef.current = node
    if (typeof forwardedRef === 'function') forwardedRef(node)
    else if (forwardedRef) forwardedRef.current = node
  }

  useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate
  }, [indeterminate])

  return (
    <label
      className={cn(
        'inline-flex cursor-pointer select-none items-center gap-2 text-md',
        disabled && 'cursor-not-allowed text-text-disabled',
        className,
      )}
    >
      <span className="relative inline-flex h-4 w-4 items-center justify-center">
        <input
          ref={setRef}
          type="checkbox"
          disabled={disabled}
          className="peer absolute h-full w-full cursor-inherit opacity-0"
          aria-checked={indeterminate ? 'mixed' : undefined}
          {...rest}
        />
        <span
          aria-hidden="true"
          data-indeterminate={indeterminate || undefined}
          className={cn(
            'pointer-events-none absolute inset-0 rounded-xs border bg-surface border-border-default transition-[background,border-color] duration-fast',
            'peer-hover:border-border-strong',
            'peer-checked:bg-accent peer-checked:border-accent',
            'peer-focus-visible:shadow-focus',
            'peer-disabled:bg-muted peer-disabled:border-border-subtle',
            indeterminate && 'bg-accent border-accent',
          )}
        />
        {indeterminate ? (
          <Minus
            aria-hidden="true"
            size={10}
            strokeWidth={3}
            className="pointer-events-none relative text-on-accent"
          />
        ) : (
          <Check
            aria-hidden="true"
            size={10}
            strokeWidth={3}
            className="pointer-events-none relative text-on-accent opacity-0 transition-opacity duration-fast peer-checked:opacity-100"
          />
        )}
      </span>
      {label ? <span className="text-primary">{label}</span> : null}
    </label>
  )
})
